import React, {useState, useContext, useEffect} from 'react';
import './PgeProjectForm.scss';
import {I18nContext} from '../../contexts/i18nContext';
import {ToastContext} from '../../contexts/toastContext';
import {ProjectsContext} from '../../contexts/projectsContext';
import {SelectionsContext} from '../../contexts/selectionsContext';
import {
  Button, DateInput, Loader, LoaderFullPage, SelectInput, TextInput, CheckboxInput, Plus, Modal, SectionBox
} from '../_common';
import {
  getCurrentUserData,
  getNestedPropertyValue,
  getOptionsList,
  setNestedPropertyValue
} from '../../helpers/helpers';
import ProjectStateForm from './ProjectStateForm';
import ChangeHistory from './ChangeHistory';
const _ = require('lodash');

const PgeProjectForm = (
  {
    project,
    users,
    primaryButtonAction,
    secondaryButtonAction,
    preview,
    refreshProjectsList,
    setDataAsEdited,
    refreshSelectedProject
  }
) => {
  const { t } = useContext(I18nContext);
  const { displayToast } = useContext(ToastContext);
  const {
    addProjectPending,
    projectStatePending,
    updatePgeProjectState,
    updatePgeProject,
    getSinglePgeProject,
    error,
  } = useContext(ProjectsContext);
  const { pending, getSelections, selections } = useContext(SelectionsContext);

  useEffect(() => {
    getSelections();
  }, []);

  const usersOptions = users.map(user => ({id: user.id, label: user.firstName + " " + user.lastName}));
  const subcompanyOptions = getOptionsList(selections, 'Firma Wolte');
  const orderTypeOptions = getOptionsList(selections, 'PGE typ zlecenia');
  const pgeRegionOptions = getOptionsList(selections, 'PGE rejon');
  const documentTypeOptions = getOptionsList(selections, 'PGE rodzaj dokumentu');
  const connectionPointOptions = getOptionsList(selections, 'PGE punkty przyłączeniowe');
  const cableDiameterOptions = getOptionsList(selections, 'PGE przekroje kabli');
  const oversightUserOptions = getOptionsList(selections, 'Elektromonterzy');
  const carsOptions = getOptionsList(selections, 'PGE samochody');
  const pipeTypeOptions = getOptionsList(selections, 'PGE typ rury');
  const pipeDiameterOptions = getOptionsList(selections, 'PGE średnica rury');
  const inspectorContactDetailsOptions = getOptionsList(
    selections,
    'PGE zawiadomienie inspektora o terminie prac budowlanych'
  );

  const [showProjectStateModal, setShowProjectStateModal] = useState(false);
  const [tab, setTab] = useState(project?.projectState === 'implementation' ? 'implementation' : 'project');
  const [formValues, setFormValues] = useState({
    companyId: project?.companyId ?? "",
    orderType: project?.orderType ?? "",
    googleDriveLink: project?.googleDriveLink ?? "",
    investmentAddress: project?.investmentAddress ?? '',
    pgeRegionId: project?.pgeRegionId ?? '',
    orderNumber: project?.orderNumber ?? '',
    conditionsNumber: project?.conditionsNumber ?? '',
    frameworkAgreementNumber: project?.frameworkAgreementNumber ?? '',
    personResponsibleForProjectId: project
      ? project?.personResponsibleForProjectId
      : getCurrentUserData().id,
    personResponsibleForImplementationId: project?.personResponsibleForImplementationId ?? '',
    originalDueDate: project?.originalDueDate ?? '',
    dueDate: project?.dueDate ?? '',
    contractorDueDate: project?.contractorDueDate ?? '',
    cost: project?.cost ?? '',
    clientContactDetails: {
      address: project?.clientContactDetails?.address ?? '',
      phoneNumber: project?.clientContactDetails?.phoneNumber ?? '',
      email: project?.clientContactDetails?.email ?? '',
      fullName: project?.clientContactDetails?.fullName ?? ''
    },
    comments: project?.comments ?? '',
    projectState: project?.projectState ?? 'project',
    appendices: project
      ? [...project?.appendices.filter(item => !!item.appendixDate && !!item.dueDate)]
      : [],
    project: {
      geodesy: {
        complete: project?.project?.geodesy?.complete ?? false,
        groundsRegistryExtract: {
          inDate: project?.project?.geodesy?.groundsRegistryExtract?.inDate ?? '',
          outDate: project?.project?.geodesy?.groundsRegistryExtract?.outDate ?? ''
        },
        baseMap: {
          inDate: project?.project?.geodesy?.baseMap?.inDate ?? '',
          outDate: project?.project?.geodesy?.baseMap?.outDate ?? ''
        },
        projectMap: {
          inDate: project?.project?.geodesy?.projectMap?.inDate ?? '',
          outDate: project?.project?.geodesy?.projectMap?.outDate ?? '',
          geodesyComment: project?.project?.geodesy?.projectMap?.geodesyComment ?? ''
        }
      },
      terrainInventory: {
        complete: project?.project?.terrainInventory?.complete ?? false,
        completeDate: project?.project?.terrainInventory?.completeDate ?? '',
      },
      pgeClientLocationAgreement: {
        complete: project?.project?.pgeClientLocationAgreement?.complete ?? false,
        ucpDateSentToClient: project?.project?.pgeClientLocationAgreement?.ucpDateSentToClient ?? '',
        corrections: project
          ? [...project?.project?.pgeClientLocationAgreement?.corrections]
          : [{date: '', comment: ''}],
        ucpDateSentToPGE: project?.project?.pgeClientLocationAgreement?.ucpDateSentToPGE ?? '',
        ucpDateReceivedFromPGE: project?.project?.pgeClientLocationAgreement?.ucpDateReceivedFromPGE ?? ''
      },
      pgeLocationAgreement: {
        complete: project?.project?.pgeLocationAgreement?.complete ?? false,
        conceptsSentDates: project
          ? [...project?.project?.pgeLocationAgreement?.conceptsSentDates]
          : [''],
        comment: project?.project?.pgeLocationAgreement?.comment ?? '',
        acceptanceDate: project?.project?.pgeLocationAgreement?.acceptanceDate ?? '',
      },
      conditionsChange: {
        complete: project?.project?.conditionsChange?.complete ?? false,
        inDate: project?.project?.conditionsChange?.inDate ?? '',
        outDate: project?.project?.conditionsChange?.outDate ?? ''
      },
      rightsToTheLocation: {
        complete: project?.project?.rightsToTheLocation?.complete ?? false,
        parcels: project
          ? [...project?.project?.rightsToTheLocation?.parcels]
          : [{parcelNumber: '', comment: '', documentType: ''}],
        applicationForLocation: {
          office: project?.project?.rightsToTheLocation?.applicationForLocation?.office ?? '',
          inDate: project?.project?.rightsToTheLocation?.applicationForLocation?.inDate ?? '',
          outDate: project?.project?.rightsToTheLocation?.applicationForLocation?.outDate ?? ''
        },
        additionalAgreements: project
          ? [...project?.project?.rightsToTheLocation?.additionalAgreements]
          : [{inDate: '', outDate: '', comment: ''}],
        por: {
          manager: {
            inDate: project?.project?.rightsToTheLocation?.por?.manager?.inDate ?? '',
            outDate: project?.project?.rightsToTheLocation?.por?.manager?.outDate ?? '',
          },
          police: {
            inDate: project?.project?.rightsToTheLocation?.por?.police?.inDate ?? '',
            outDate: project?.project?.rightsToTheLocation?.por?.police?.outDate ?? '',
          },
          county: {
            inDate: project?.project?.rightsToTheLocation?.por?.county?.inDate ?? '',
            outDate: project?.project?.rightsToTheLocation?.por?.county?.outDate ?? '',
          },
          porDetails: project
            ? [...project?.project?.rightsToTheLocation?.por?.porDetails]
            : [{name: '', inDate: '', outDate: '', comment: ''}],
        }
      },
      zud: {
        inDate: project?.project?.zud?.inDate ?? '',
        outDate: project?.project?.zud?.outDate ?? ''
      },
      projectAgreements: project
        ? [...project?.project?.projectAgreements]
        : [{
          inDate: '',
          agreementSide: '',
          signDate: '',
          comments: [{comment: '', inDate: '', modificationDate: '', outDate: ''}]
        }],
    },
    implementation: {
      staking: {
        stakingSent: project?.implementation?.staking?.stakingSent ?? false,
        stakingSentDate: project?.implementation?.staking?.stakingSentDate ?? '',
        surveyor: project?.implementation?.staking?.surveyor ?? '',
      },
      parcelPartAcquisitionsComments: project?.implementation?.parcelPartAcquisitionsComments ?? '',
      parcelPartAcquisitions: project
        ? [...project.implementation.parcelPartAcquisitions]
        : [{
          parcelId: '',
          administrationOffice: '',
          applicationDate: '',
          applicationMethod: '',
          guarantee: '',
          acquisitionDateFrom: '',
          acquisitionDateTo:'',
          decisionNumber: '',
          decisionDate: '',
          decisionCost: '',
          inspectorData: '',
          inspectionDate: '',
          constructionDateFrom: '',
          constructionDateTo: '',
          surfaceReconstruction: {
            contractor: '',
            date: '',
            comment: '',
            enabled: false
          },
          acquisitionRetrievalDate: '',
          acquisitionRetrievalProtocolDate: '',
          acquisitionRetrievalProtocol: '',
          decisionPaidFor: false,
          inspectorRequired: false
        }],
      warranties: project
        ? [...project?.implementation?.warranties]
        : [{name: '', applicationDate: '', applicationReceivedDate: '', comment: ''}],
      materials: {
        connectionPoints: project
          ? [...project?.implementation?.materials?.connectionPoints]
          : [''],
        cables: project
          ? [...project?.implementation?.materials?.cables]
          : [{diameterSelectionId: '', pathLength: '', cableLength: ''}],
        connectionTypes: project
          ? [...project?.implementation?.materials?.connectionTypes]
          : [''],
        lengthOfTheSqueeze: project
          ? [...project?.implementation?.materials?.lengthOfTheSqueeze]
          : [''],
        pipe: {
          pipeType: project?.implementation?.materials?.pipeType ?? '',
          pipeDiameter: project?.implementation?.materials?.pipeDiameter ?? '',
          pipeLength: project?.implementation?.materials?.pipeLength ?? '',
        }
      },
      construction: {
        inspectorContactDetails: project?.implementation?.construction?.inspectorContactDetails ?? '',
        inspectorContactDate: project?.implementation?.construction?.inspectorContactDate ?? '',
        constructionDate: project?.implementation?.construction?.constructionDate ?? '',
        exclusionDate: project?.implementation?.construction?.exclusionDate ?? '',
        leadUserId: project?.implementation?.construction?.leadUserId ?? '',
        pgeOversight: project?.implementation?.construction?.pgeOversight ?? false,
        wolteOversightUserId: project?.implementation?.construction?.wolteOversightUserId ?? '',
        crewMechanicIds: project
          ? [...project?.implementation?.construction?.crewMechanicIds.filter(x => !!x)]
          : [],
        carIds: project
          ? [...project?.implementation?.construction?.carIds.filter(x => !!x)]
          : [],
        comment: project?.implementation?.construction?.comment ?? '',
        photoUploaded: project?.implementation?.construction?.photoUploaded ?? false,
      },
      fixes: project
        ? [
          ...project?.implementation?.fixes
            .map(x => (
              { ...x, crewMechanicsIds: x.crewMechanicsIds.filter(y => !!y), carIds: x.carIds.filter(y => !!y)}
            ))
        ]
        : [{date: '', type: '', crewMechanicsIds: [], carIds: []}],
      documentation: {
        inspectorNotes: project?.implementation?.documentation?.inspectorNotes ?? false,
        measurementProtocol: project?.implementation?.documentation?.measurementProtocol ?? false,
        measurementProtocolDate: project?.implementation?.documentation?.measurementProtocolDate ?? '',
        documentsFromOversight: project?.implementation?.documentation?.documentsFromOversight ?? false,
        documentsFromOversightReceivedAt: project?.implementation?.documentation?.documentsFromOversightReceivedAt ?? '',
        inventory: project?.implementation?.documentation?.inventory ?? false,
        inventoryRequestedAt: project?.implementation?.documentation?.inventoryRequestedAt ?? '',
        inventoryReceivedAt: project?.implementation?.documentation?.inventoryReceivedAt ?? '',
        inventoryForwardedToPgeDate: project?.implementation?.documentation?.inventoryForwardedToPgeDate ?? '',
        documentationForwardedToPGE: project?.implementation?.documentation?.documentationForwardedToPGE ?? '',
        submittedForRecieval: project?.implementation?.documentation?.submittedForRecieval ?? '',
        receivedProtocol: project?.implementation?.documentation?.receivedProtocol ?? '',
        invoiceSent: project?.implementation?.documentation?.invoiceSent ?? ''
      }
    },
    projectStateChanges: project ? project.projectStateChanges : []
  });

  const orderType = project?.orderType
    ? orderTypeOptions.find(x => x.id === project?.orderType).label
    : undefined;

  useEffect(() => {
    if (project) {
      setFormValues(project);
    }
  }, [project]);

  const changeTab = tabId => {
    if (tabId !== tab) {
      setTab(tabId)
    }
  };

  const changeValue = async (name, value, multiple, blur) => {
    setDataAsEdited();
    const newValues = {...formValues};

    if (multiple) {
      if (!getNestedPropertyValue(newValues, name).includes(value.id ? value.id : value)) {
        const newValue = [...getNestedPropertyValue(newValues, name), value.id ? value.id : value];
        const updatedValues = setNestedPropertyValue(newValues, name, newValue);
        setFormValues(updatedValues);
      } else {
        const newValue = [...getNestedPropertyValue(newValues, name)];
        const index = newValue.indexOf(value.id ? value.id : value);
        newValue.splice(index, 1);
        const updatedValues = setNestedPropertyValue(newValues, name, newValue);
        setFormValues(updatedValues);
      }
    } else {
      if (name.includes(".")) {
        const updatedValues = setNestedPropertyValue(newValues, name, value);
        setFormValues(updatedValues);
      } else {
        newValues[name] = value.id ? value.id : value;
        setFormValues(newValues);
      }
    }

    if (blur) {
      await saveOnBlur(name, value.id ? value.id : value, multiple);
    }
  };

  const changeOrderTypeValue = async (name, value, multiple, blur) => {
    setDataAsEdited();
    const newValues = {...formValues};
    newValues[name] = value.id ? value.id : value;
    setFormValues(newValues);

    if (blur) {
      await saveOnBlur(name, value.id ? value.id : value, multiple);
    }

    const type = orderTypeOptions.find(x => x.id === value).label;

    if (!!project && type === 'Tylko projekt') {
      setTab('project');
      await updatePgeProjectState(
        'project',
        project.id,
        t('state changed due to project type change')
      );
    } else if (!!project && type === 'Tylko realizacja') {
      setTab('implementation');
      await updatePgeProjectState(
        'implementation',
        project.id,
        t('state changed due to project type change')
      );
    }
  };

  const formHasEmptyFields = () => {
    if (!project) {
      const emptyOptionInputs = [
        formValues.companyId,
        formValues.orderType,
        formValues.investmentAddress,
        formValues.pgeRegionId,
        formValues.personResponsibleForProjectId,
        formValues.orderNumber,
        formValues.conditionsNumber,
        formValues.frameworkAgreementNumber,
        formValues.originalDueDate,
        formValues.cost
      ].filter(item => !item);
      return !!emptyOptionInputs.length;
    } else {
      return false;
    }
  };

  const formHasInvalidAppendices = () => {
    if (!!project) {
      const invalidAppendices = formValues.appendices
        .filter(item => !item.filingDate);
      return !!invalidAppendices.length
    } else {
      return false;
    }
  };

  const toggleProjectStateModal = () => {
    setShowProjectStateModal(!showProjectStateModal);
  };

  const changeProjectState = async comment => {
    await updatePgeProjectState(
      project.projectState === 'project'
        ? 'implementation'
        : 'project',
      project.id,
      comment
    );
    refreshProjectsList();
    setShowProjectStateModal(false);
    secondaryButtonAction(true);
  };

  const saveOnBlur = async (name, value, multiple) => {
    if (!!project) {
      const nameArray = name.split('.');
      let newValue = nameArray.length ? {[nameArray[0]]: formValues[nameArray[0]]} : {};

      if (multiple) {
        if (!getNestedPropertyValue(newValue, name).includes(value.id ? value.id : value)) {
          const updatedValue = [...getNestedPropertyValue(newValue, name), value.id ? value.id : value];
          newValue = setNestedPropertyValue(newValue, name, updatedValue);
        } else {
          const updatedValue = [...getNestedPropertyValue(newValue, name)];
          const index = updatedValue.indexOf(value.id ? value.id : value);
          updatedValue.splice(index, 1);
          newValue = setNestedPropertyValue(newValue, name, updatedValue);
        }
      } else {
        if (name.includes(".")) {
          newValue = setNestedPropertyValue(newValue, name, value);
        } else {
          newValue[name] = value;
        }
      }
      const result = await updatePgeProject(newValue, project?.id, project?.version, true);

      if (result.status && result.status === 412) {
        const newProjectObj = await getSinglePgeProject(project.id);

        const currentValue = getNestedPropertyValue(project, name);
        const updatedValue = getNestedPropertyValue(newProjectObj, name);

        if (_.isEqual(currentValue, updatedValue)) {
          let updatedNewValue = nameArray.length ? {[nameArray[0]]: newProjectObj[nameArray[0]]} : {};
          if (name.includes(".")) {
            updatedNewValue = setNestedPropertyValue(updatedNewValue, name, value);
          } else {
            updatedNewValue[name] = value;
          }
          await updatePgeProject(updatedNewValue, project?.id, newProjectObj?.version, true);
          const updatedProjectObj = await getSinglePgeProject(project.id);
          await refreshSelectedProject(updatedProjectObj);
          displayToast(
            'info',
            t("Project data has been updated by other user. The form data has been updated.", "asIs"),
            7000
          );
        } else {
          await refreshSelectedProject(newProjectObj);
          await changeValue(name, updatedValue);
          displayToast(
            'error',
            t(
              "The value which you are trying to edit has been updated by other user. The form data has been updated. Please try again if required.",
              "asIs"
            ),
            0,
            true
          );
        }
      } else {
        const newProjectObj = await getSinglePgeProject(project.id);
        await refreshSelectedProject(newProjectObj);
      }
    }
  };

  if (pending) return <div className='loaderWrapper'><Loader /></div>

  return (
    <form className='PgeProjectForm'>
      {!!addProjectPending && (
        <LoaderFullPage  />
      )}
      <div className='formInputs'>
        <SectionBox label={t('header')} expandedByDefault>
          <SelectInput
            name='companyId'
            label={t('subcompany', 'asIs')}
            options={subcompanyOptions.length ? subcompanyOptions : []}
            onSelect={changeValue}
            value={formValues.companyId}
            placeholder={t('select')}
            required
            disabled={preview}
          />
          <SelectInput
            name='orderType'
            label={t('order type')}
            options={orderTypeOptions.length ? orderTypeOptions : []}
            onSelect={changeOrderTypeValue}
            value={formValues.orderType}
            placeholder={t('select')}
            required
            disabled={preview}
          />
          <TextInput
            label={t('google drive link')}
            name='googleDriveLink'
            onChange={changeValue}
            value={formValues.googleDriveLink}
            disabled={preview}
            link
          />
          <TextInput
            label={t('investment address')}
            required
            name='investmentAddress'
            onChange={changeValue}
            value={formValues.investmentAddress}
            disabled={preview}
          />
          <SelectInput
            name='pgeRegionId'
            label={t('pge region', 'asIs')}
            options={pgeRegionOptions.length ? pgeRegionOptions : []}
            onSelect={changeValue}
            value={formValues.pgeRegionId}
            placeholder={t('select')}
            required
            disabled={preview}
          />
          <SelectInput
            name='personResponsibleForProjectId'
            label={t('person responsible for the project')}
            options={usersOptions}
            onSelect={changeValue}
            value={formValues.personResponsibleForProjectId}
            placeholder={t('select')}
            required
            disabled={preview}
          />
          {!!project && (
            <SelectInput
              name='personResponsibleForImplementationId'
              label={t('person responsible for the implementation')}
              options={usersOptions}
              onSelect={changeValue}
              value={formValues.personResponsibleForImplementationId}
              placeholder={t('select')}
              disabled={preview}
            />
          )}
          <TextInput
            label={t('order number')}
            required
            name='orderNumber'
            onChange={changeValue}
            value={formValues.orderNumber}
            disabled={preview}
          />
          <TextInput
            label={t('conditions number')}
            required
            name='conditionsNumber'
            onChange={changeValue}
            value={formValues.conditionsNumber}
            disabled={preview}
          />
          <TextInput
            label={t('framework agreement number')}
            required
            name='frameworkAgreementNumber'
            onChange={changeValue}
            value={formValues.frameworkAgreementNumber}
            disabled={preview}
          />
          <DateInput
            label={t('due date')}
            required={!project || !project.appendices.length}
            name={!project || !project.appendices.length ? 'originalDueDate' : 'dueDate'}
            value={!project || !project.appendices.length ? formValues.originalDueDate: formValues.dueDate}
            onChange={changeValue}
            disabled={preview || !!project?.appendices?.length}
          />
          <DateInput
            label={t('contractor agreement due date')}
            name='contractorDueDate'
            value={formValues.contractorDueDate}
            onChange={changeValue}
            disabled={preview}
          />
          <TextInput
            type='number'
            label={t('order cost')}
            required
            name='cost'
            onChange={changeValue}
            value={formValues.cost}
            disabled={preview}
          />
          {!!project && orderType !== 'Tylko projekt' && (
            <DateInput
              label={t('construction date')}
              name='implementation.construction.constructionDate'
              value={formValues.implementation.construction.constructionDate}
              onChange={changeValue}
              disabled={true}
            />
          )}
          <div className='groupBox'>
            <div className='groupBoxLabel'>
              {t('client contact details')}
            </div>
            <TextInput
              label={t('address')}
              name='clientContactDetails.address'
              onChange={changeValue}
              value={formValues.clientContactDetails.address}
              disabled={preview}
            />
            <TextInput
              label={t('phone number')}
              name='clientContactDetails.phoneNumber'
              onChange={changeValue}
              value={formValues.clientContactDetails.phoneNumber}
              disabled={preview}
            />
            <TextInput
              label={t('e-mail')}
              name='clientContactDetails.email'
              onChange={changeValue}
              value={formValues.clientContactDetails.email}
              disabled={preview}
            />
            <TextInput
              label={t('client name')}
              name='clientContactDetails.fullName'
              onChange={changeValue}
              value={formValues.clientContactDetails.fullName}
              disabled={preview}
            />
          </div>
          <TextInput
            label={t('remarks')}
            name='comments'
            onChange={changeValue}
            value={formValues.comments}
            disabled={preview}
            area
          />
          {!!project && (
            <>
              <TextInput
                label={t('project state')}
                name='projectState'
                onChange={changeValue}
                value={t(formValues.projectState)}
                disabled={true}
              />
              {!!project && !!project.projectStateChanges.length && (
                <ChangeHistory
                  stateChangeHistory={project.projectStateChanges}
                  changeLog={project.changelog}
                  usersOptions={usersOptions}
                />
              )}
            </>
          )}
        </SectionBox>

        {!!project && (
          <>
            <div className='mainSectionLabel'>
              {t('appendices')}
            </div>
            <SectionBox label={t('appendices')}>
              <Plus
                name='appendices'
                value={formValues.appendices}
                itemObject={{appendixDate: '', dueDate: '', googleDriveLink: ''}}
                onChange={changeValue}
                disabled={preview}
                itemInputs={[
                  {type: 'date', name: 'appendixDate', label: t('appendix date'), required: false},
                  {type: 'date', name: 'dueDate', label: t('due date'), required: false},
                  {type: 'text', name: 'googleDriveLink', label: t('google drive link'), required: false, link: true},
                  {type: 'date', name: 'filingDate', label: t('application submission date'), required: true},
                  {type: 'text', name: 'comment', label: t('remarks'), required: false}
                ]}
              />
              {formHasInvalidAppendices() && (
                <div className='sectionError'>{t('all appendices must have filing date filled in')}</div>
              )}
            </SectionBox>
          </>
        )}

        {!!project && (
          <div className='tabBar'>
            {orderType !== 'Tylko realizacja' && (
              <div
                className={tab === 'project' ? 'tabButton--selected' : 'tabButton'}
                onClick={() => changeTab('project')}
              >
                {t('project')}
              </div>
            )}
            {orderType !== 'Tylko projekt' && (
              <div
                className={tab === 'implementation' ? 'tabButton--selected' : 'tabButton'}
                onClick={() => changeTab('implementation')}
              >
                {t('implementation')}
              </div>
            )}
          </div>
        )}

        {!!project && tab === 'project' && (
          <>
            <SectionBox label={t('geodesy')}>
              <CheckboxInput
                name='project.geodesy.complete'
                value={formValues.project.geodesy.complete}
                onChange={changeValue}
                label={t('completed')}
                disabled={preview}
              />
              <div className='groupBox'>
                <div className='groupBoxLabel'>
                  {t('ground registry extract')}
                </div>
                <DateInput
                  label={t('in date')}
                  name='project.geodesy.groundsRegistryExtract.inDate'
                  value={formValues.project.geodesy.groundsRegistryExtract.inDate}
                  onChange={changeValue}
                  disabled={preview}
                />
                <DateInput
                  label={t('out date')}
                  name='project.geodesy.groundsRegistryExtract.outDate'
                  value={formValues.project.geodesy.groundsRegistryExtract.outDate}
                  onChange={changeValue}
                  disabled={preview}
                />
              </div>
              <div className='groupBox'>
                <div className='groupBoxLabel'>
                  {t('base map')}
                </div>
                <DateInput
                  label={t('in date')}
                  name='project.geodesy.baseMap.inDate'
                  value={formValues.project.geodesy.baseMap.inDate}
                  onChange={changeValue}
                  disabled={preview}
                />
                <DateInput
                  label={t('out date')}
                  name='project.geodesy.baseMap.outDate'
                  value={formValues.project.geodesy.baseMap.outDate}
                  onChange={changeValue}
                  disabled={preview}
                />
              </div>
              <div className='groupBox'>
                <div className='groupBoxLabel'>
                  {t('project map')}
                </div>
                <DateInput
                  label={t('in date')}
                  name='project.geodesy.projectMap.inDate'
                  value={formValues.project.geodesy.projectMap.inDate}
                  onChange={changeValue}
                  disabled={preview}
                />
                <DateInput
                  label={t('out date')}
                  name='project.geodesy.projectMap.outDate'
                  value={formValues.project.geodesy.projectMap.outDate}
                  onChange={changeValue}
                  disabled={preview}
                />
                <TextInput
                  label={t('surveyor')}
                  name='project.geodesy.projectMap.geodesyComment'
                  onChange={changeValue}
                  value={formValues.project.geodesy.projectMap.geodesyComment}
                  disabled={preview}
                />
              </div>
            </SectionBox>
            <SectionBox label={t('terrain inventory')}>
              <CheckboxInput
                name='project.terrainInventory.complete'
                value={formValues.project.terrainInventory.complete}
                onChange={changeValue}
                label={t('completed')}
                disabled={preview}
              />
              <DateInput
                label={t('complete date')}
                name='project.terrainInventory.completeDate'
                value={formValues.project.terrainInventory.completeDate}
                onChange={changeValue}
                disabled={preview}
              />
            </SectionBox>
            <SectionBox label={t('location agreement with client')}>
              <CheckboxInput
                name='project.pgeClientLocationAgreement.complete'
                value={formValues.project.pgeClientLocationAgreement.complete}
                onChange={changeValue}
                label={t('completed')}
                disabled={preview}
              />
              <DateInput
                label={t('UCP sent to client', 'asIs')}
                name='project.pgeClientLocationAgreement.ucpDateSentToClient'
                value={formValues.project.pgeClientLocationAgreement.ucpDateSentToClient}
                onChange={changeValue}
                disabled={preview}
              />
              <Plus
                label={t('corrections')}
                name='project.pgeClientLocationAgreement.corrections'
                value={formValues.project.pgeClientLocationAgreement.corrections}
                itemObject={{date: '', comment: ''}}
                onChange={changeValue}
                disabled={preview}
                itemInputs={[
                  {type: 'date', name: 'date', label: t('date'), required: false},
                  {type: 'text', name: 'comment', label: t('comment'), required: false}
                ]}
              />
              <DateInput
                label={t('ucp sent to pge', 'asIs')}
                name='project.pgeClientLocationAgreement.ucpDateSentToPGE'
                value={formValues.project.pgeClientLocationAgreement.ucpDateSentToPGE}
                onChange={changeValue}
                disabled={preview}
              />
              <DateInput
                label={t('ucp received from pge', 'asIs')}
                name='project.pgeClientLocationAgreement.ucpDateReceivedFromPGE'
                value={formValues.project.pgeClientLocationAgreement.ucpDateReceivedFromPGE}
                onChange={changeValue}
                disabled={preview}
              />
            </SectionBox>
            <SectionBox label={t('pge devices location agreement', 'asIs')}>
              <CheckboxInput
                name='project.pgeLocationAgreement.complete'
                value={formValues.project.pgeLocationAgreement.complete}
                onChange={changeValue}
                label={t('completed')}
                disabled={preview}
              />
              <Plus
                label={t('concepts sent dates')}
                name='project.pgeLocationAgreement.conceptsSentDates'
                value={formValues.project.pgeLocationAgreement.conceptsSentDates}
                itemObject={''}
                onChange={changeValue}
                disabled={preview}
                itemInputs={[
                  {type: 'date', name: '', label: t('date'), required: false}
                ]}
              />
              <TextInput
                label={t('comment')}
                name='project.pgeLocationAgreement.comment'
                onChange={changeValue}
                value={formValues.project.pgeLocationAgreement.comment}
                disabled={preview}
              />
              <DateInput
                label={t('acceptance received')}
                name='project.pgeLocationAgreement.acceptanceDate'
                value={formValues.project.pgeLocationAgreement.acceptanceDate}
                onChange={changeValue}
                disabled={preview}
              />
            </SectionBox>
            <SectionBox label={t('conditions change')}>
              <CheckboxInput
                name='project.conditionsChange.complete'
                value={formValues.project.conditionsChange.complete}
                onChange={changeValue}
                label={t('completed')}
                disabled={preview}
              />
              <DateInput
                label={t('in date')}
                name='project.conditionsChange.inDate'
                value={formValues.project.conditionsChange.inDate}
                onChange={changeValue}
                disabled={preview}
              />
              <DateInput
                label={t('out date')}
                name='project.conditionsChange.outDate'
                value={formValues.project.conditionsChange.outDate}
                onChange={changeValue}
                disabled={preview}
              />
            </SectionBox>
            <SectionBox label={t('rights to the location')}>
              <CheckboxInput
                name='project.rightsToTheLocation.complete'
                value={formValues.project.rightsToTheLocation.complete}
                onChange={changeValue}
                label={t('completed')}
                disabled={preview}
              />
              <Plus
                label={t('parcels')}
                name='project.rightsToTheLocation.parcels'
                value={formValues.project.rightsToTheLocation.parcels}
                itemObject={{parcelNumber: '', comment: '', documentType: ''}}
                onChange={changeValue}
                disabled={preview}
                itemInputs={[
                  {type: 'text', name: 'parcelNumber', label: t('parcel number'), required: false},
                  {type: 'text', name: 'comment', label: t('comment'), required: false},
                  {
                    type: 'select',
                    name: 'documentType',
                    label: t('document type'),
                    options: documentTypeOptions,
                    required: false
                  }
                ]}
              />
              <div className='groupBox'>
                <div className='groupBoxLabel'>
                  {t('application for location')}
                </div>
                <TextInput
                  label={t('office')}
                  name='project.rightsToTheLocation.applicationForLocation.office'
                  onChange={changeValue}
                  value={formValues.project.rightsToTheLocation.applicationForLocation.office}
                  disabled={preview}
                />
                <DateInput
                  label={t('in date')}
                  name='project.rightsToTheLocation.applicationForLocation.inDate'
                  value={formValues.project.rightsToTheLocation.applicationForLocation.inDate}
                  onChange={changeValue}
                  disabled={preview}
                />
                <DateInput
                  label={t('out date')}
                  name='project.rightsToTheLocation.applicationForLocation.outDate'
                  value={formValues.project.rightsToTheLocation.applicationForLocation.outDate}
                  onChange={changeValue}
                  disabled={preview}
                />
              </div>
              <Plus
                label={t('additional agreements')}
                name='project.rightsToTheLocation.additionalAgreements'
                value={formValues.project.rightsToTheLocation.additionalAgreements}
                itemObject={{inDate: '', outDate: '', comment: ''}}
                onChange={changeValue}
                disabled={preview}
                itemInputs={[
                  {type: 'date', name: 'inDate', label: t('in date'), required: false},
                  {type: 'date', name: 'outDate', label: t('out date'), required: false},
                  {type: 'text', name: 'comment', label: t('comment'), required: false}
                ]}
              />
              <div className='groupBox'>
                <div className='groupBoxLabel'>
                  {t('por', 'upper')}
                </div>
                <div className='groupBox'>
                  <div className='groupBoxLabel'>
                    {t('manager')}
                  </div>
                  <DateInput
                    label={t('in date')}
                    name='project.rightsToTheLocation.por.manager.inDate'
                    value={formValues.project.rightsToTheLocation.por.manager.inDate}
                    onChange={changeValue}
                    disabled={preview}
                  />
                  <DateInput
                    label={t('out date')}
                    name='project.rightsToTheLocation.por.manager.outDate'
                    value={formValues.project.rightsToTheLocation.por.manager.outDate}
                    onChange={changeValue}
                    disabled={preview}
                  />
                </div>
                <div className='groupBox'>
                  <div className='groupBoxLabel'>
                    {t('police')}
                  </div>
                  <DateInput
                    label={t('in date')}
                    name='project.rightsToTheLocation.por.police.inDate'
                    value={formValues.project.rightsToTheLocation.por.police.inDate}
                    onChange={changeValue}
                    disabled={preview}
                  />
                  <DateInput
                    label={t('out date')}
                    name='project.rightsToTheLocation.por.police.outDate'
                    value={formValues.project.rightsToTheLocation.por.police.outDate}
                    onChange={changeValue}
                    disabled={preview}
                  />
                </div>
                <div className='groupBox'>
                  <div className='groupBoxLabel'>
                    {t('county')}
                  </div>
                  <DateInput
                    label={t('in date')}
                    name='project.rightsToTheLocation.por.county.inDate'
                    value={formValues.project.rightsToTheLocation.por.county.inDate}
                    onChange={changeValue}
                    disabled={preview}
                  />
                  <DateInput
                    label={t('out date')}
                    name='project.rightsToTheLocation.por.county.outDate'
                    value={formValues.project.rightsToTheLocation.por.county.outDate}
                    onChange={changeValue}
                    disabled={preview}
                  />
                </div>
                <Plus
                  name='project.rightsToTheLocation.por.porDetails'
                  value={formValues.project.rightsToTheLocation.por.porDetails}
                  itemObject={{name: '', inDate: '', outDate: '', comment: ''}}
                  onChange={changeValue}
                  disabled={preview}
                  itemInputs={[
                    {type: 'text', name: 'name', label: t('name'), required: false},
                    {type: 'date', name: 'inDate', label: t('in date'), required: false},
                    {type: 'date', name: 'outDate', label: t('out date'), required: false},
                    {type: 'text', name: 'comment', label: t('comment'), required: false}
                  ]}
                />
              </div>
            </SectionBox>
            <SectionBox label={t('zud', 'upper')}>
              <DateInput
                label={t('in date')}
                name='project.zud.inDate'
                value={formValues.project.zud.inDate}
                onChange={changeValue}
                disabled={preview}
              />
              <DateInput
                label={t('out date')}
                name='project.zud.outDate'
                value={formValues.project.zud.outDate}
                onChange={changeValue}
                disabled={preview}
              />
            </SectionBox>
            <SectionBox label={t('project agreements')}>
              <Plus
                name='project.projectAgreements'
                value={formValues.project.projectAgreements}
                itemObject={{inDate: '', agreementSide: '', signDate: '', comments: []}}
                onChange={changeValue}
                disabled={preview}
                itemInputs={[
                  {type: 'date', name: 'inDate', label: t('in date'), required: false},
                  {type: 'text', name: 'agreementSide', label: t('agreement side'), required: false},
                  {type: 'date', name: 'signDate', label: t('sign date'), required: false},
                ]}
                nestedPlusName='comments'
                nestedPlusObject={{comment: '', inDate: '', modificationDate: '', outDate: ''}}
                nestedPlusItemInputs={[
                  {type: 'text', name: 'comment', label: t('comment'), required: false},
                  {type: 'date', name: 'inDate', label: t('comment in date'), required: false},
                  {type: 'date', name: 'modificationDate', label: t('modification date'), required: false},
                  {type: 'date', name: 'outDate', label: t('comment out date'), required: false}
                ]}
              />
            </SectionBox>
          </>
        )}
        {!!project && tab === 'implementation' && (
          <>
            <SectionBox label={t('staking')}>
              <CheckboxInput
                name='implementation.staking.stakingSent'
                value={formValues.implementation.staking.stakingSent}
                onChange={changeValue}
                label={t('staking sent')}
                disabled={preview}
              />
              {formValues.implementation.staking.stakingSent && (
                <DateInput
                  label={t('staking sent date')}
                  name='implementation.staking.stakingSentDate'
                  value={formValues.implementation.staking.stakingSentDate}
                  onChange={changeValue}
                  disabled={preview}
                />
              )}
              <TextInput
                label={t('surveyor')}
                name='implementation.staking.surveyor'
                onChange={changeValue}
                value={formValues.implementation.staking.surveyor}
                disabled={preview}
              />
            </SectionBox>
            <SectionBox label={t('rights to the location')}>
              <TextInput
                label={t('remarks')}
                name='implementation.parcelPartAcquisitionsComments'
                onChange={changeValue}
                value={formValues.implementation.parcelPartAcquisitionsComments}
                disabled={preview}
                area
              />
              {project.project.rightsToTheLocation.parcels.map((parcel, index) => (
                <div className='groupBox' key={index}>
                  <div className='groupBoxLabel'>
                    {t('parcel')}: {parcel.parcelNumber + ', '}
                    {t('document type')}: {' '}
                    {documentTypeOptions?.length
                      ? documentTypeOptions.find(x => x.id === parcel.documentType)?.label
                      : ''
                    }
                  </div>
                  {documentTypeOptions.find(x => x.id === parcel.documentType)?.label !== 'UCP' && (
                    <Plus
                      label={t('strip of land acquisition')}
                      name='implementation.parcelPartAcquisitions'
                      value={formValues.implementation.parcelPartAcquisitions}
                      itemObject={{
                        parcelId: parcel.parcelNumber,
                        documentType: parcel.documentType,
                        administrationOffice: project?.project?.rightsToTheLocation?.applicationForLocation?.office ?? '',
                        applicationMethod: '',
                        guarantee: '',
                        acquisitionDateFrom: '',
                        acquisitionDateTo:'',
                        decisionNumber: '',
                        decisionDate: '',
                        decisionCost: '',
                        inspectorData: '',
                        inspectionDate: '',
                        constructionDateFrom: '',
                        constructionDateTo: '',
                        surfaceReconstruction: {
                          contractor: '',
                          date: '',
                          comment: '',
                          enabled: false
                        },
                        acquisitionRetrievalDate: '',
                        acquisitionRetrievalProtocol: '',
                        decisionPaidFor: false,
                        inspectorRequired: false
                      }}
                      onChange={changeValue}
                      disabled={preview}
                      itemInputs={[
                        {type: 'text', name: 'parcelId', label: t('parcel'), required: false, disabled: true},
                        {
                          type: 'text',
                          name: 'administrationOffice',
                          label: t('office'),
                          required: false,
                          disabled: !!project?.project?.rightsToTheLocation?.applicationForLocation?.office
                        },
                        {type: 'date', name: 'applicationDate', label: t('application date'), required: false},
                        {type: 'text', name: 'applicationMethod', label: t('application method'), required: false},
                        {type: 'textarea', name: 'guarantee', label: t('guarantee'), required: false},
                        {type: 'date', name: 'acquisitionDateFrom', label: t('acquisition date from'), required: false},
                        {type: 'date', name: 'acquisitionDateTo', label: t('acquisition date to'), required: false},
                        {type: 'text', name: 'decisionNumber', label: t('decision number'), required: false},
                        {type: 'date', name: 'decisionDate', label: t('decision date'), required: false},
                        {type: 'text', name: 'decisionCost', label: t('decision cost'), required: false},
                        {type: 'checkbox', name: 'decisionPaidFor', label: t('decision paid'), required: false},
                        {type: 'checkbox', name: 'inspectorRequired', label: t('inspector required'), required: false},
                        {
                          type: 'text',
                          name: 'inspectorData',
                          label: t('inspector data'),
                          required: false,
                          visible: 'inspectorRequired'
                        },
                        {
                          type: 'date',
                          name: 'inspectionDate',
                          label: t('inspection date'),
                          required: false,
                          visible: 'inspectorRequired'
                        },
                        {type: 'separator'},
                        {type: 'date', name: 'constructionDateFrom', label: t('construction date from'), required: false},
                        {type: 'date', name: 'constructionDateTo', label: t('construction date to'), required: false},
                        {
                          type: 'checkbox',
                          name: 'surfaceReconstruction.enabled',
                          label: t('surface reconstruction'),
                          required: false
                        },
                        {
                          type: 'text',
                          name: 'surfaceReconstruction.contractor',
                          label: t('contractor'),
                          required: false,
                          visible: 'surfaceReconstruction.enabled'
                        },
                        {
                          type: 'date',
                          name: 'surfaceReconstruction.date',
                          label: t('date'),
                          required: false,
                          visible: 'surfaceReconstruction.enabled'
                        },
                        {
                          type: 'text',
                          name: 'surfaceReconstruction.comment',
                          label: t('comment'),
                          required: false,
                          visible: 'surfaceReconstruction.enabled'
                        },
                        {type: 'separator'},
                        {
                          type: 'date',
                          name: 'acquisitionRetrievalDate',
                          label: t('acquisition retrieval date'),
                          required: false
                        },
                        {type: 'separator'},
                        {
                          type: 'date',
                          name: 'acquisitionRetrievalProtocolDate',
                          label: t('acquisition retrieval protocol date'),
                          required: false
                        },
                        {
                          type: 'text',
                          name: 'acquisitionRetrievalProtocol',
                          label: t('acquisition retrieval protocol'),
                          required: false
                        },
                      ]}
                      displayedItemsFilter='parcelId'
                      displayedItemsFilterValue={parcel.parcelNumber}
                    />
                  )}
                </div>
              ))}
            </SectionBox>
            <SectionBox label={t('supervision / warranty acquisition')}>
              <Plus
                name='implementation.warranties'
                value={formValues.implementation.warranties}
                itemObject={{name: '', applicationDate: '', applicationReceivedDate: '', contact: ''}}
                onChange={changeValue}
                disabled={preview}
                itemInputs={[
                  {type: 'text', name: 'name', label: t('name'), required: false},
                  {type: 'date', name: 'applicationDate', label: t('application date'), required: false},
                  {type: 'date', name: 'applicationReceivedDate', label: t('application receival date'), required: false},
                  {type: 'text', name: 'contact', label: t('contact'), required: false}
                ]}
              />
            </SectionBox>
            <SectionBox label={t('materials')}>
              <Plus
                label={t('connection points')}
                name='implementation.materials.connectionPoints'
                value={formValues.implementation.materials.connectionPoints}
                itemObject={''}
                onChange={changeValue}
                disabled={preview}
                itemInputs={[
                  {
                    type: 'select',
                    name: '',
                    label: t('connection point'),
                    options: connectionPointOptions,
                    placeholder: t('select'),
                    required: false
                  }
                ]}
              />
              <Plus
                label={t('cables')}
                name='implementation.materials.cables'
                value={formValues.implementation.materials.cables}
                itemObject={{diameterSelectionId: '', pathLength: '', cableLength: ''}}
                onChange={changeValue}
                disabled={preview}
                itemInputs={[
                  {
                    type: 'select',
                    name: 'diameterSelectionId',
                    label: t('diameter'),
                    options: cableDiameterOptions,
                    required: false
                  },
                  {type: 'text', name: 'pathLength', label: t('path length'), required: false},
                  {type: 'text', name: 'cableLength', label: t('cable length'), required: false}
                ]}
              />
              <Plus
                label={t('connection types')}
                name='implementation.materials.connectionTypes'
                value={formValues.implementation.materials.connectionTypes}
                itemObject={''}
                onChange={changeValue}
                disabled={preview}
                itemInputs={[
                  {type: 'text', name: '', label: t('connection type'), required: false}
                ]}
              />
              <Plus
                label={t('lengths of squeeze')}
                name='implementation.materials.lengthOfTheSqueeze'
                value={formValues.implementation.materials.lengthOfTheSqueeze}
                itemObject={''}
                onChange={changeValue}
                disabled={preview}
                itemInputs={[
                  {type: 'text', name: '', label: t('length of squeeze'), required: false}
                ]}
              />
              <div className='groupBox'>
                <div className='groupBoxLabel'>
                  {t('pipe')}
                </div>
                <SelectInput
                  name='implementation.materials.pipe.pipeType'
                  label={t('type')}
                  options={pipeTypeOptions}
                  onSelect={changeValue}
                  value={formValues.implementation.materials.pipe.pipeType}
                  placeholder={t('select')}
                  disabled={preview}
                />
                <SelectInput
                  name='implementation.materials.pipe.pipeDiameter'
                  label={t('diameter')}
                  options={pipeDiameterOptions}
                  onSelect={changeValue}
                  value={formValues.implementation.materials.pipe.pipeDiameter}
                  placeholder={t('select')}
                  disabled={preview}
                />
                <TextInput
                  label={t('length')}
                  name='implementation.materials.pipe.pipeLength'
                  onChange={changeValue}
                  value={formValues.implementation.materials.pipe.pipeLength}
                  disabled={preview}
                />
              </div>
            </SectionBox>
            <SectionBox label={t('construction work')}>
              <SelectInput
                name='implementation.construction.inspectorContactDetails'
                label={t('notifying inspector of construction dates')}
                options={inspectorContactDetailsOptions}
                onSelect={changeValue}
                value={formValues.implementation.construction.inspectorContactDetails}
                placeholder={t('select')}
                disabled={preview}
              />
              <DateInput
                label={t('contact date')}
                name='implementation.construction.inspectorContactDate'
                value={formValues.implementation.construction.inspectorContactDate}
                onChange={changeValue}
                disabled={preview}
              />
              <div className='separator' />
              <DateInput
                label={t('construction date')}
                name='implementation.construction.constructionDate'
                value={formValues.implementation.construction.constructionDate}
                onChange={changeValue}
                disabled={preview}
              />
              <div className='groupBox'>
                <div className='groupBoxLabel'>
                  {t('ppn / exclusion', 'asIs')}
                </div>
                <DateInput
                  label={t('date')}
                  name='implementation.construction.exclusionDate'
                  value={formValues.implementation.construction.exclusionDate}
                  onChange={changeValue}
                  disabled={preview}
                />
                <SelectInput
                  name='implementation.construction.leadUserId'
                  label={t('lead')}
                  options={oversightUserOptions}
                  onSelect={changeValue}
                  value={formValues.implementation.construction.leadUserId}
                  placeholder={t('select')}
                  disabled={preview}
                />
                <CheckboxInput
                  name='implementation.construction.pgeOversight'
                  value={formValues.implementation.construction.pgeOversight}
                  onChange={changeValue}
                  label={t('PGE oversight', 'asIs')}
                  disabled={preview}
                />
                {!formValues.implementation.construction.pgeOversight && (
                  <SelectInput
                    name='implementation.construction.wolteOversightUserId'
                    label={t('oversight user')}
                    options={oversightUserOptions}
                    onSelect={changeValue}
                    value={formValues.implementation.construction.wolteOversightUserId}
                    placeholder={t('select')}
                    disabled={preview}
                  />
                )}
                <SelectInput
                  name='implementation.construction.crewMechanicIds'
                  label={t('construction crew')}
                  options={oversightUserOptions}
                  onSelect={changeValue}
                  value={formValues.implementation.construction.crewMechanicIds}
                  placeholder={t('select')}
                  disabled={preview}
                  multiple
                />
                <SelectInput
                  name='implementation.construction.carIds'
                  label={t('equipment')}
                  options={carsOptions}
                  onSelect={changeValue}
                  value={formValues.implementation.construction.carIds}
                  placeholder={t('select')}
                  disabled={preview}
                  multiple
                />
                <TextInput
                  label={t('remarks')}
                  name='implementation.construction.comment'
                  onChange={changeValue}
                  value={formValues.implementation.construction.comment}
                  disabled={preview}
                  area
                />
                <CheckboxInput
                  name='implementation.construction.photoUploaded'
                  value={formValues.implementation.construction.photoUploaded}
                  onChange={changeValue}
                  label={t('photos uploaded to drive')}
                  disabled={preview}
                />
              </div>
            </SectionBox>
            <SectionBox label={t('fixes')}>
              <Plus
                name='implementation.fixes'
                value={formValues.implementation.fixes}
                itemObject={{date: '', type: '', crewMechanicsIds: [], carIds: []}}
                onChange={changeValue}
                disabled={preview}
                itemInputs={[
                  {type: 'date', name: 'date', label: t('date'), required: false},
                  {type: 'text', name: 'type', label: t('type'), required: false},
                  {
                    type: 'select',
                    name: 'crewMechanicsIds',
                    label: t('construction crew'),
                    options: oversightUserOptions,
                    multiple: true,
                    required: false
                  },
                  {
                    type: 'select',
                    name: 'carIds',
                    label: t('equipment'),
                    options: carsOptions,
                    multiple: true,
                    required: false
                  }
                ]}
              />
            </SectionBox>
            <SectionBox label={t('as-built documentation')}>
              <CheckboxInput
                name='implementation.documentation.inspectorNotes'
                value={formValues.implementation.documentation.inspectorNotes}
                onChange={changeValue}
                label={t("inspector's notes")}
                disabled={preview}
              />
              <div className='groupBox'>
                <div className='groupBoxLabel'>
                  {t('measurement protocols')}
                </div>
                <CheckboxInput
                  name='implementation.documentation.measurementProtocol'
                  value={formValues.implementation.documentation.measurementProtocol}
                  onChange={changeValue}
                  label={t('commissioned')}
                  disabled={preview}
                />
                {formValues.implementation.documentation.measurementProtocol && (
                  <DateInput
                    label={t('requested date')}
                    name='implementation.documentation.measurementProtocolDate'
                    value={formValues.implementation.documentation.measurementProtocolDate}
                    onChange={changeValue}
                    disabled={preview}
                  />)}
              </div>
              <div className='groupBox'>
                <CheckboxInput
                  name='implementation.documentation.documentsFromOversight'
                  value={formValues.implementation.documentation.documentsFromOversight}
                  onChange={changeValue}
                  label={t('supervision documents')}
                  disabled={preview}
                />
                {formValues.implementation.documentation.documentsFromOversight && (
                  <DateInput
                    label={t('date')}
                    name='implementation.documentation.documentsFromOversightReceivedAt'
                    value={formValues.implementation.documentation.documentsFromOversightReceivedAt}
                    onChange={changeValue}
                    disabled={preview}
                  />
                )}
              </div>
              <div className='groupBox'>
                <CheckboxInput
                  name='implementation.documentation.inventory'
                  value={formValues.implementation.documentation.inventory}
                  onChange={changeValue}
                  label={t('inventory')}
                  disabled={preview}
                />
                {formValues.implementation.documentation.inventory && (
                  <>
                    <DateInput
                      label={t('commissioned')}
                      name='implementation.documentation.inventoryRequestedAt'
                      value={formValues.implementation.documentation.inventoryRequestedAt}
                      onChange={changeValue}
                      disabled={preview}
                    />
                    <DateInput
                      label={t('received')}
                      name='implementation.documentation.inventoryReceivedAt'
                      value={formValues.implementation.documentation.inventoryReceivedAt}
                      onChange={changeValue}
                      disabled={preview}
                    />
                    <DateInput
                      label={t('delivered to pge', 'asIs')}
                      name='implementation.documentation.inventoryForwardedToPgeDate'
                      value={formValues.implementation.documentation.inventoryForwardedToPgeDate}
                      onChange={changeValue}
                      disabled={preview}
                    />
                  </>
                )}
              </div>
              <DateInput
                label={t('submitted for acquisition')}
                name='implementation.documentation.submittedForRecieval'
                value={formValues.implementation.documentation.submittedForRecieval}
                onChange={changeValue}
                disabled={preview}
              />
              <DateInput
                label={t('protocol received')}
                name='implementation.documentation.receivedProtocol'
                value={formValues.implementation.documentation.receivedProtocol}
                onChange={changeValue}
                disabled={preview}
              />
              <DateInput
                label={t('invoice issued')}
                name='implementation.documentation.invoiceSent'
                value={formValues.implementation.documentation.invoiceSent}
                onChange={changeValue}
                disabled={preview}
              />
            </SectionBox>
          </>
        )}
      </div>
      <div className='formButtons'>
        <Button type='secondary' action={secondaryButtonAction} label={t('close')} />
        {!!project && project.projectState !== 'archived' && !preview && (
          <Button
            type='primary'
            action={toggleProjectStateModal}
            label={project.projectState === 'project' ? t('send for implementation') : t('return to project phase')}
            margin='left'
            disabled={projectStatePending || formHasInvalidAppendices()}
            pending={projectStatePending}
          />
        )}
        {!!primaryButtonAction && !project && (
          <Button
            type='primary'
            action={() => primaryButtonAction(formValues, project?.id, project?.version)}
            label={!project ? t('add project'): t('save')}
            margin='left'
            disabled={addProjectPending || formHasEmptyFields() || formHasInvalidAppendices()}
            pending={addProjectPending}
          />
        )}
      </div>

      {!!showProjectStateModal && (
        <Modal closeModal={toggleProjectStateModal} title={t('change project state')} width='310px'>
          <ProjectStateForm
            newState={project.projectState === 'project' ? 'implementation' : 'project'}
            primaryButtonAction={changeProjectState}
            secondaryButtonAction={toggleProjectStateModal}
          />
        </Modal>
      )}
    </form>
  );
};

export default PgeProjectForm;
